import React from "react";
import "./PrivacyPolicy.css";

const PrivacyPolicy = () => {
  return (
    <div className="privacy-page">
      <div className="privacy-container">
        <h1>Privacy Policy</h1>
        <p className="effective-date">Effective Date: April 1, 2025</p>

        <section className="privacy-section">
          <h2>1. Introduction</h2>
          <p>
            Diviti Adora Infrared & Red Light Studio ("we", "us", or "our") is
            committed to protecting your privacy. This Privacy Policy explains
            how we collect, use, disclose, and safeguard your personal
            information when you visit our website, use our services, or
            interact with us in any other way. Please read this policy carefully
            to understand our views and practices regarding your personal data.
          </p>
        </section>

        <section className="privacy-section">
          <h2>2. Information We Collect</h2>
          <h3>2.1 Personal Information</h3>
          <p>
            We may collect personal information that you voluntarily provide to
            us, including:
          </p>
          <ul>
            <li>
              <strong>Contact Information:</strong> Name, email address, postal
              address, and phone number.
            </li>
            <li>
              <strong>Account Information:</strong> Usernames, passwords, and
              other registration details.
            </li>
            <li>
              <strong>Payment Information:</strong> Credit card details and
              billing address (processed securely by our payment provider).
            </li>
            <li>
              <strong>Health Information:</strong> Information related to your
              wellness, which may be shared in the context of booking a session
              or consulting with our staff.
            </li>
          </ul>

          <h3>2.2 Non-Personal Information</h3>
          <p>We may automatically collect non-personal information such as:</p>
          <ul>
            <li>Browser type, operating system, and device information.</li>
            <li>
              Usage data, including pages visited, links clicked, and other
              actions on our website.
            </li>
            <li>
              IP address and geolocation data, used in accordance with
              applicable laws.
            </li>
          </ul>
        </section>

        <section className="privacy-section">
          <h2>3. How We Use Your Information</h2>
          <p>We use the collected information to:</p>
          <ul>
            <li>Provide, operate, and maintain our services.</li>
            <li>Process transactions and send confirmations or invoices.</li>
            <li>Improve our website and enhance user experience.</li>
            <li>
              Communicate with you about promotions, updates, and other relevant
              information.
            </li>
            <li>
              Ensure the security of our systems and prevent fraudulent
              activity.
            </li>
            <li>Comply with legal obligations and enforce our policies.</li>
          </ul>
        </section>

        <section className="privacy-section">
          <h2>4. Cookies and Tracking Technologies</h2>
          <p>
            Our website may use cookies and similar tracking technologies to
            enhance your user experience, analyze site usage, and deliver
            personalized content. You can control or disable cookies through
            your browser settings, though this may affect your ability to use
            some features of our site.
          </p>
        </section>

        <section className="privacy-section">
          <h2>5. Data Sharing and Disclosure</h2>
          <h3>5.1 Third-Party Service Providers</h3>
          <p>
            We may share your personal information with trusted third-party
            vendors who perform services on our behalf, such as payment
            processing, data analysis, marketing, and customer service. These
            third parties are contractually obligated to maintain the
            confidentiality and security of your information.
          </p>

          <h3>5.2 Legal Requirements</h3>
          <p>
            We may disclose your information if required by law or in response
            to a subpoena, court order, or other legal process, or to protect
            our rights, privacy, safety, or property, as well as that of our
            users or the public.
          </p>

          <h3>5.3 Business Transfers</h3>
          <p>
            If Diviti Adora Infrared & Red Light Studio is involved in a merger,
            acquisition, or asset sale, your information may be transferred as
            part of that transaction. We will notify you via email and/or a
            prominent notice on our website of any change in ownership or use of
            your personal information.
          </p>
        </section>

        <section className="privacy-section">
          <h2>6. Data Retention</h2>
          <p>
            We will retain your personal information only for as long as
            necessary to fulfill the purposes for which it was collected or as
            required by applicable laws and regulations. Once this information
            is no longer needed, we will take reasonable steps to securely
            delete or anonymize it.
          </p>
        </section>

        <section className="privacy-section">
          <h2>7. Data Security</h2>
          <p>
            We implement reasonable administrative, technical, and physical
            measures to protect your personal information from unauthorized
            access, use, or disclosure. While we strive to secure your data,
            please be aware that no security measures are completely foolproof.
          </p>
        </section>

        <section className="privacy-section">
          <h2>8. Your Rights and Choices</h2>
          <h3>8.1 Access and Correction</h3>
          <p>
            You have the right to access, update, or correct your personal
            information held by us. If you need assistance, please contact us
            using the details provided below.
          </p>

          <h3>8.2 Opt-Out</h3>
          <p>
            You may opt-out of receiving marketing communications from us by
            following the unsubscribe instructions included in those
            communications or by contacting us directly.
          </p>

          <h3>8.3 Data Portability and Deletion</h3>
          <p>
            Subject to applicable laws, you may have the right to request that
            we provide a copy of your personal data in a structured, commonly
            used format or request its deletion. Please note that certain
            information may be retained for legal or administrative purposes.
          </p>
        </section>

        <section className="privacy-section">
          <h2>9. Children's Privacy</h2>
          <p>
            Our services are not directed toward children under the age of 13
            (or under the age required by local law). We do not knowingly
            collect personal information from children. If we become aware that
            we have inadvertently collected such information, we will take steps
            to delete it promptly.
          </p>
        </section>

        <section className="privacy-section">
          <h2>10. International Data Transfers</h2>
          <p>
            Your personal information may be transferred to and maintained on
            servers located in countries outside of your jurisdiction. We will
            take appropriate steps to ensure that any international transfers of
            your information comply with applicable data protection laws.
          </p>
        </section>

        <section className="privacy-section">
          <h2>11. Changes to This Privacy Policy</h2>
          <p>
            We reserve the right to update or modify this Privacy Policy at any
            time. Any changes will be effective immediately upon posting on our
            website, with an updated effective date. We encourage you to review
            this policy periodically to stay informed about how we are
            protecting your information.
          </p>
        </section>

        <section className="privacy-section">
          <h2>12. Contact Us</h2>
          <p>
            If you have any questions, concerns, or requests regarding this
            Privacy Policy, please contact us at:
          </p>
          <address>
            Diviti Adora Infrared & Red Light Studio
            <br />
            1930 E Tangerine Rd
            <br />
            Suite 100
            <br />
            Oro Valley, AZ 85737
            <br />
            Email:{" "}
            <a href="mailto:info@divitiadora.com">info@divitiadora.com</a>
          </address>
        </section>
      </div>
    </div>
  );
};

export default PrivacyPolicy;
